import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { StepCreate } from "./step-create.js";

// ---------------------------------------------------------------------------
// Types (retained for compatibility with dormant wizard step modules)
// ---------------------------------------------------------------------------

export type WizardStep = "create" | "sources" | "generate";

export type EntryPoint = "url" | "github" | "describe";

export interface SourceEntry {
  id: string;
  backendSourceId: string | null;
  name: string;
  url?: string;
  type: "url" | "file" | "github";
  status: "pending" | "ingesting" | "processing" | "done" | "error";
  processingStep?: string;
  errorMessage?: string;
  crawlProgress?: number;
  currentTitle?: string;
}

export interface UrlAnalysisResult {
  suggestedName: string;
  suggestedDescription: string;
  context: Record<string, unknown> | null;
  glossarySuggestions: Array<{
    term: string;
    definition: string;
    language: string;
  }>;
  analysisText: string;
  featureSuggestions: string[];
}

export interface WizardState {
  step: WizardStep;
  completedSteps: Set<WizardStep>;
  entryPoint: EntryPoint | null;
  seedUrl: string | null;
  seedGithubRepoId: string | null;
  name: string;
  description: string;
  projectId: string | null;
  sources: SourceEntry[];
  urlAnalysis: UrlAnalysisResult | null;
  urlAnalyzing: boolean;
  featureSuggestions: string[];
  selectedFeature: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProjectWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeProjectId = searchParams.get("project");
  const isMock = searchParams.get("mock") === "true";

  if (resumeProjectId) {
    return <Navigate to={`/projects/${resumeProjectId}`} replace />;
  }

  function handleClose() {
    navigate("/");
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 sm:gap-8 px-4 py-3 sm:px-8 sm:py-5">
          <div className="flex items-center gap-2 sm:gap-6">
            <span className="text-lg font-semibold leading-none tracking-tight">
              iamu
            </span>
            <span className="hidden sm:inline text-sm font-medium text-muted-foreground">
              New project
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-8 sm:py-16">
        <StepCreate
          onComplete={(id) => navigate(`/projects/${id}`)}
          isMock={isMock}
        />
      </div>
    </div>
  );
}
